import { useRef, useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../../features/auth/store'
import { useTenantStore } from '../stores/useTenantStore'
import { useSearchMerchants, useMerchant, useMerchantsPreview } from '../../features/merchants/hooks/useMerchants'

const RECENT_KEY = 'osprey:recent_merchants'
const MAX_RECENT = 5

type RecentEntry = { id: string; name: string; slug: string }

const readRecent = (): RecentEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
  } catch {
    return []
  }
}

const pushRecent = (merchant: RecentEntry) => {
  const prev = readRecent().filter((m) => m.id !== merchant.id)
  localStorage.setItem(RECENT_KEY, JSON.stringify([merchant, ...prev].slice(0, MAX_RECENT)))
}

const useDebounce = (value: string, delay: number) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const MerchantOption = ({
  merchant,
  selectedId,
  label,
  onSelect,
}: {
  merchant: RecentEntry
  selectedId: string | null
  label?: string
  onSelect: (m: RecentEntry) => void
}) => (
  <li
    onMouseDown={() => onSelect(merchant)}
    className={`cursor-pointer px-5 py-3 hover:bg-muted transition-colors ${
      merchant.id === selectedId ? 'bg-muted font-semibold text-foreground' : ''
    }`}
  >
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="truncate text-foreground">
        {merchant.name}
        <span className="ml-2 text-muted-foreground text-xs font-normal">({merchant.slug})</span>
      </span>
      {label && (
        <span className="shrink-0 text-xs text-muted-foreground font-normal">{label}</span>
      )}
    </div>
  </li>
)

export const MerchantPicker = () => {
  const { selectedTenantId, setSelectedTenantId } = useAuthStore()
  // Keep the other "current merchant" store (useTenantStore.activeMerchant, read by Orders,
  // Dashboard, Payouts and the Header label) in lock-step with this picker's selectedTenantId,
  // so the two never diverge and land a write on the wrong tenant.
  const activeMerchant = useTenantStore((s) => s.activeMerchant)
  const setActiveMerchant = useTenantStore((s) => s.setActiveMerchant)
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [recent, setRecent] = useState<RecentEntry[]>(readRecent)
  const containerRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounce(inputValue, 250)
  const { data: searchResults = [], isFetching } = useSearchMerchants(debouncedQuery)
  const { data: previewMerchants = [] } = useMerchantsPreview(5)

  const { data: selectedMerchant } = useMerchant(selectedTenantId ?? '')

  // Mirror the full selected merchant into activeMerchant once it loads, so both stores agree.
  useEffect(() => {
    if (selectedMerchant && selectedMerchant.id !== activeMerchant?.id) {
      setActiveMerchant(selectedMerchant)
    }
  }, [selectedMerchant, activeMerchant?.id, setActiveMerchant])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = useCallback((entry: RecentEntry) => {
    setSelectedTenantId(entry.id)
    pushRecent(entry)
    setRecent(readRecent())
    setInputValue('')
    setOpen(false)
  }, [setSelectedTenantId])

  const handleClear = () => {
    setSelectedTenantId(null)
    setActiveMerchant(null)
    setInputValue('')
  }

  const displayValue = selectedMerchant
    ? `${selectedMerchant.name} (${selectedMerchant.slug})`
    : ''

  const isSearching = debouncedQuery.length >= 1

  return (
    <div className="relative w-full">
      <div ref={containerRef} className="relative w-full">
        {selectedTenantId && !open ? (
          <div className="flex items-center gap-3 rounded-md border border-[#E2E2E5] bg-muted/50 px-5 h-[48px] text-sm transition-all duration-150">
            <span className="flex-1 truncate text-foreground font-medium">{displayValue || '…'}</span>
            <button
              onClick={() => { setOpen(true); setInputValue('') }}
              className="shrink-0 text-xs text-muted-foreground font-semibold hover:text-foreground underline transition-all"
            >
              Change
            </button>
            <button
              onClick={handleClear}
              className="shrink-0 text-muted-foreground hover:text-muted-foreground transition-colors text-sm"
              aria-label="Clear"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="relative flex items-center h-[48px]">
            <input
              type="text"
              autoComplete="off"
              placeholder="Search merchants…"
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setOpen(true) }}
              onFocus={() => setOpen(true)}
              className="w-full h-full rounded-md border border-[#E2E2E5] bg-card px-5 text-sm focus:outline-none focus:border-gray-400 transition-all placeholder-gray-400"
            />
            {inputValue && (
              <button
                onClick={() => setInputValue('')}
                className="absolute right-4 text-muted-foreground hover:text-muted-foreground text-sm"
                aria-label="Clear Input"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {open && (
          <ul className="absolute z-50 mt-1.5 w-full overflow-y-auto rounded-md border border-[#E2E2E5] bg-card text-sm shadow-lg max-h-64 py-1">
            {isSearching ? (
              isFetching ? (
                <li className="px-5 py-3 text-muted-foreground">Searching…</li>
              ) : searchResults.length === 0 ? (
                <li className="px-5 py-3 text-muted-foreground">No merchants found</li>
              ) : (
                searchResults.map((m) => (
                  <MerchantOption
                    key={m.id}
                    merchant={{ id: m.id, name: m.name, slug: m.slug }}
                    selectedId={selectedTenantId}
                    onSelect={handleSelect}
                  />
                ))
              )
            ) : (
              <>
                {recent.length > 0 && (
                  <>
                    <li className="px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground select-none">
                      Recently viewed
                    </li>
                    {recent.map((m) => (
                      <MerchantOption
                        key={m.id}
                        merchant={m}
                        selectedId={selectedTenantId}
                        onSelect={handleSelect}
                      />
                    ))}
                  </>
                )}
                {(() => {
                  const filler = previewMerchants
                    .filter((m) => !recent.some((r) => r.id === m.id))
                    .slice(0, MAX_RECENT - recent.length)
                  if (filler.length === 0) return null
                  return (
                    <>
                      <li className="px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground select-none">
                        Recently added
                      </li>
                      {filler.map((m) => (
                        <MerchantOption
                          key={m.id}
                          merchant={{ id: m.id, name: m.name, slug: m.slug }}
                          selectedId={selectedTenantId}
                          onSelect={handleSelect}
                        />
                      ))}
                    </>
                  )
                })()}
                <li className="mx-5 my-1.5 border-t border-border" />
                <li className="px-5 py-2 text-[10px] text-muted-foreground font-medium">
                  Type to search all merchants
                </li>
              </>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}
